import React from 'react';
import { Container, Typography, Box, Chip, Divider } from '@mui/material';
import {
  NewReleases,
  BugReport,
  Tune,
  Star,
  Code,
  Security,
  Speed
} from '@mui/icons-material';
import './WhatsNew.scss';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { useTheme } from '../../hooks/ThemeProvider';

interface ChangelogEntry {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  categories: {
    features?: string[];
    improvements?: string[];
    bugFixes?: string[];
    security?: string[];
    performance?: string[];
  };
}

const changelogData: ChangelogEntry[] = [
  {
    version: "0.2.0",
    date: "2025-08-05",
    type: "major",
    categories: {
      features: [
        "Complete profile system overhaul - Settings is now 'My Profile' with enhanced user experience",
        "Profile photo upload functionality",
        "Interactive preferences quiz to personalize your recommendations",
        "Enhanced account settings with notification preferences"
      ],
      improvements: [
        "Redesigned profile interface with glassmorphic cards and smooth animations",
        "Improved user onboarding with personalized taste discovery",
        "Better visual hierarchy and information architecture",
        "Enhanced mobile responsiveness across all profile components"
      ],
      performance: [
        "Efficient image upload and preview system",
        "Smooth card transitions and micro-interactions"
      ]
    }
  },
  {
    version: "0.1.5",
    date: "2025-07-14",
    type: "patch",
    categories: {
      performance: [
        "Improved map performance by displaying relevant places based on view bounds",
        "Optimized marker filtering and bounds handling"
      ],
      improvements: [
        "Updated activity data structure for better location handling"
      ],
      bugFixes: [
        "Fixed analytics API errors"
      ]
    }
  },
  {
    version: "0.1.4",
    date: "2025-07-12",
    type: "major",
    categories: {
      features: [
        "Revamped map markers to display better and more accurate information"
      ],
      performance: [
        "Major performance improvements for displaying map markers"
      ],
      improvements: [
        "Optimized backend for better system performance"
      ]
    }
  },
  {
    version: "0.1.3",
    date: "2025-07-09",
    type: "patch",
    categories: {
      improvements: [
        "Enhanced system reliability with better backend connection handling",
        "Improved error handling"
      ]
    }
  },
  {
    version: "0.1.2",
    date: "2025-07-09",
    type: "minor",
    categories: {
      improvements: [
        "Added changelog functionality to keep users informed of updates and improvements",
        "Updated iconography for profile dropdown with improved visual design and better user experience"
      ]
    }
  },
  {
    version: "0.1.1",
    date: "2025-07-08",
    type: "minor",
    categories: {
      bugFixes: [
        "Fixed iOS navigation bar visibility issue that affected certain devices when using the browser",
        "Streamlined manual account creation process for improved user experience"
      ]
    }
  },
  {
    version: "0.1",
    date: "2025-07-07",
    type: "major",
    categories: {
      features: [
        "Official app launch - thank you to our dedicated beta users for their invaluable feedback and testing"
      ]
    }
  }
];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'features':
      return <Star className="category-icon" />;
    case 'improvements':
      return <Tune className="category-icon" />;
    case 'bugFixes':
      return <BugReport className="category-icon" />;
    case 'security':
      return <Security className="category-icon" />;
    case 'performance':
      return <Speed className="category-icon" />;
    default:
      return <Code className="category-icon" />;
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'features':
      return 'New Features';
    case 'improvements':
      return 'Improvements';
    case 'bugFixes':
      return 'Bug Fixes';
    case 'security':
      return 'Security';
    case 'performance':
      return 'Performance';
    default:
      return category;
  }
};

const getVersionColor = (type: string) => {
  switch (type) {
    case 'major':
      return 'error';
    case 'minor':
      return 'primary';
    case 'patch':
      return 'success';
    default:
      return 'default';
  }
};

const WhatsNew: React.FC = () => {
  useTheme();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Container maxWidth="md" className="whats-new-container fade-in">
      <Box className="whats-new-header" sx={{ textAlign: 'center', mb: 4 }}>
        <NewReleases className="header-icon" sx={{ fontSize: 32, mb: 1 }} />
        <Typography variant="h4" component="h1" gutterBottom>
          What's New?
        </Typography>
        <Typography variant="subtitle1" className="whats-new-subtitle" color="text.secondary">
          Stay up to date with the latest features and improvements
        </Typography>
      </Box>

      <Box className="changelog-timeline">
        {changelogData.map((entry, index) => (
          <GlassmorphicCard
            key={entry.version}
            className={`changelog-entry slide-up`}
            sx={{ mb: 3, animationDelay: `${index * 0.1}s` }}
          >
            <Box className="changelog-header">
              <Box className="version-info">
                <Typography variant="h5" component="h2" className="version-number">
                  Version {entry.version}
                </Typography>
                <Typography variant="body2" className="release-date" color="text.secondary">
                  {formatDate(entry.date)}
                </Typography>
              </Box>
              <Chip
                label={entry.type}
                color={getVersionColor(entry.type) as any}
                size="small"
                className="version-chip"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box className="changelog-content">
              {Object.entries(entry.categories).map(([category, items]) => (
                items && items.length > 0 && (
                  <Box key={category} className="category-section">
                    <Box className="category-header">
                      {getCategoryIcon(category)}
                      <Typography variant="h6" className="category-title">
                        {getCategoryLabel(category)}
                      </Typography>
                    </Box>
                    <ul className="change-list">
                      {items.map((item, itemIndex) => (
                        <li key={itemIndex} className="change-item">
                          <Typography variant="body2" className="change-text">
                            {item}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )
              ))}
            </Box>
          </GlassmorphicCard>
        ))}
      </Box>

      <Box className="more-updates-info" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          For any issues or questions, please reach out to{' '}
          <a href="mailto:admin@mindara.io">
            admin@mindara.io
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default WhatsNew;